import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { FinalCTA } from "@/components/home/FinalCTA";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  afsServiceSchema,
  afsFaqSchema,
  generateBreadcrumbSchema,
  organizationSchema,
  localBusinessSchema,
} from "@/lib/structuredData";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  FileText,
  Building2,
  UserCheck,
  Shield,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const afsServices = [
  {
    icon: FileText,
    title: "Annual Financial Statements (AFS)",
    description:
      "We prepare complete annual financial statements including the statement of financial position (balance sheet), statement of comprehensive income, statement of changes in equity, cash flow statement, and accounting policy notes — all in compliance with IFRS for SMEs or full IFRS as applicable.",
  },
  {
    icon: Building2,
    title: "CIPC Annual Returns",
    description:
      "Every registered company in South Africa must file an annual return with CIPC within 30 business days of its registration anniversary. We track your deadline and ensure on-time submission to prevent deregistration.",
  },
  {
    icon: UserCheck,
    title: "Beneficial Ownership (BO) Filing",
    description:
      "Since April 2023, all South African companies must file a beneficial ownership register with CIPC. We identify and register the natural persons who own or control your company, keeping you compliant with the Companies Amendment Act.",
  },
  {
    icon: Shield,
    title: "Workmen's Compensation (COIDA)",
    description:
      "If you employ staff, you must register with the Compensation Fund and submit an annual return of earnings. We handle the calculation and submission to ensure your employees are covered and your business is compliant.",
  },
];

const AnnualFinancialStatements = () => {
  return (
    <Layout>
      <SEO
        title="Annual Financial Statements & CIPC Returns | South Africa"
        description="Annual financial statements, CIPC returns, beneficial ownership, and COIDA filings by a SAICA registered CA. Stay compliant, avoid deregistration."
        canonical="/annual-financial-statements"
        structuredData={[
          organizationSchema,
          localBusinessSchema,
          afsServiceSchema,
          afsFaqSchema,
          generateBreadcrumbSchema([
            { name: "Home", url: "https://bookkeepingservices.co.za/" },
            { name: "Services", url: "https://bookkeepingservices.co.za/services" },
            { name: "Annual Financial Statements", url: "https://bookkeepingservices.co.za/annual-financial-statements" },
          ]),
        ]}
      />

      <Breadcrumbs items={[{ name: "Services", href: "/services" }, { name: "Annual Financial Statements" }]} />



      {/* Hero */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/30" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-sm font-medium text-primary mb-4 tracking-wide uppercase animate-fade-up">
              Financial Statements & Compliance
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 animate-fade-up">
              Annual Financial Statements
              <span className="block text-primary">& CIPC Compliance</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed animate-fade-up max-w-2xl mx-auto mb-8">
              Annual financial statements are a legal requirement for all registered companies 
              in South Africa. They provide a formal record of your business's financial 
              performance and are needed for SARS, CIPC, banks, and investors.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up">
              <Link to="/book-a-call">
                <Button size="lg" className="btn-primary gap-2 text-base px-8 h-14">
                  Get a Free Quote
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/packages">
                <Button size="lg" variant="outline" className="btn-outline-primary gap-2 text-base px-8 h-14">
                  View Packages
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="What's Included"
            title="Statements & Compliance Services"
            description="Professional preparation by a SAICA registered Chartered Accountant."
          />
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {afsServices.map((service) => (
              <div key={service.title} className="card-elevated p-8">
                <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center text-primary mb-5">
                  <service.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground mb-3">
                  {service.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What AFS Includes — AI-optimised content block */}
      <section className="py-20 lg:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="AFS Components"
            title="What's in Your Financial Statements?"
            description="A complete set of annual financial statements includes these key reports."
          />
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              { title: "Statement of Financial Position (Balance Sheet)", desc: "A snapshot of your company's assets, liabilities, and equity at year-end." },
              { title: "Statement of Comprehensive Income", desc: "Your revenue, expenses, and profit or loss for the financial year." },
              { title: "Statement of Changes in Equity", desc: "How your company's equity changed during the year — including retained earnings and dividends." },
              { title: "Cash Flow Statement", desc: "Where your cash came from and where it went — operating, investing, and financing activities." },
              { title: "Accounting Policies & Notes", desc: "Detailed notes explaining the accounting policies used and providing additional context for the numbers." },
            ].map((item, i) => (
              <div key={i} className="p-5 rounded-xl bg-card border border-border">
                <h3 className="font-heading text-base font-semibold text-foreground mb-1">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* E-E-A-T */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="Why Us"
            title="Professionally Prepared by a SAICA CA"
            description="Your financial statements deserve the highest standard of preparation."
          />
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              "SAICA registered Chartered Accountant — the gold standard in South African accounting",
              "Compliant with IFRS for SMEs and full IFRS standards",
              "Accepted by SARS, CIPC, banks, and financial institutions",
              "We track all your compliance deadlines and submit proactively",
              "Integrated with your monthly bookkeeping for seamless year-end preparation",
              "Clear communication — we explain your financials in plain language",
            ].map((point, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-muted/50">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm text-foreground">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="FAQ"
            title="Financial Statements & CIPC Questions"
            description="Common questions about annual financial statements and company compliance."
          />
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-3">
              {afsFaqSchema.mainEntity.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-card border border-border rounded-xl px-6 hover:border-primary/20 transition-colors duration-300 data-[state=open]:border-primary/30 data-[state=open]:shadow-md"
                >
                  <AccordionTrigger className="text-left font-heading text-base font-semibold hover:no-underline hover:text-primary transition-colors py-5">
                    {faq.name}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                    {faq.acceptedAnswer.text}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <FinalCTA />
    </Layout>
  );
};

export default AnnualFinancialStatements;
